"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHtmlPersonalization = exports.PERSONALIZATION_TIMEOUT_MS = void 0;
const react_1 = require("react");
const js_cookie_1 = __importDefault(require("js-cookie"));
const useAlloy_1 = require("./useAlloy");
const pageBatches = new Map();
const pageResultCache = new Map();
const BATCH_DEBOUNCE_MS = 50;
exports.PERSONALIZATION_TIMEOUT_MS = 5000;
function getPageKey() {
    return `web://${window.location.host}${window.location.pathname}`;
}
function getSurface(pageKey, personalizationId) {
    return `${pageKey}#${personalizationId}`;
}
function getIdentityMap() {
    // Preview token handling (used for AJO testing)
    const params = new URLSearchParams(window.location.search);
    const baseToken = params.get('ajo_preview_token');
    const variant = params.get('journey_variant');
    const runId = params.get('run_id') || '0';
    const isAJOPreview = baseToken ===
        '14ba619468b5ff31ed82561b0e64471fb8fabbcd9d8abdfb1eda3f6807f31d95' &&
        !!variant;
    const identityMap = {};
    if (isAJOPreview && baseToken && variant) {
        identityMap.PreviewToken = [
            {
                id: `ajo_preview_${baseToken}_${variant}${runId}`,
                primary: true
            }
        ];
        return identityMap;
    }
    const encryptedClientId = window.sessionStorage.getItem('EncryptedClientId');
    const fpid = js_cookie_1.default.get('cs_fpid');
    if (encryptedClientId) {
        identityMap.encryptedClientID = [
            {
                id: encryptedClientId,
                authenticatedState: 'authenticated',
                primary: true
            }
        ];
    }
    if (fpid) {
        identityMap.FPID = [
            {
                id: fpid,
                authenticatedState: 'ambiguous',
                primary: false
            }
        ];
    }
    return identityMap;
}
function getSelectedProposition(propositions, personalizationId) {
    // Priority: AJO > Target > CMS
    const ajo = propositions.find(p => p.scopeDetails?.decisionProvider === 'AJO' &&
        p.scope?.includes(`#${personalizationId}`));
    const target = propositions.find(p => p.scope === personalizationId);
    return ajo || target;
}
async function resolveHtmlFromProposition(proposition, displayPropositions) {
    const rawContent = proposition.items?.[0]?.data?.content;
    if (typeof rawContent !== 'string') {
        return null;
    }
    const isAJO = proposition.scopeDetails?.decisionProvider === 'AJO';
    const trackDisplayMetric = () => {
        if (!isAJO || !proposition.id)
            return;
        displayPropositions.push({
            id: proposition.id,
            scope: proposition.scope,
            scopeDetails: proposition.scopeDetails
        });
    };
    // AJO can return JSON with a URL pointer instead of direct HTML.
    try {
        const parsed = JSON.parse(rawContent);
        if (parsed.url) {
            const response = await fetch(parsed.url);
            if (!response.ok) {
                return null;
            }
            const html = await response.text();
            trackDisplayMetric();
            return html;
        }
    }
    catch {
        // Not JSON, continue as direct HTML.
    }
    trackDisplayMetric();
    return rawContent;
}
function scheduleFlush(pageKey) {
    const batch = pageBatches.get(pageKey);
    if (!batch || batch.isFlushed) {
        return;
    }
    if (batch.flushTimer) {
        clearTimeout(batch.flushTimer);
    }
    const flushDelay = typeof document !== 'undefined' && document.readyState === 'complete'
        ? 0
        : BATCH_DEBOUNCE_MS;
    batch.flushTimer = setTimeout(() => {
        void flushBatch(pageKey);
    }, flushDelay);
}
async function flushBatch(pageKey) {
    const batch = pageBatches.get(pageKey);
    if (!batch || batch.isFlushed) {
        return;
    }
    batch.isFlushed = true;
    if (batch.flushTimer) {
        clearTimeout(batch.flushTimer);
    }
    batch.flushTimer = null;
    // Release this page key immediately so late registrations can create a
    // follow-up batch while the current request is still in flight.
    pageBatches.delete(pageKey);
    const ids = Array.from(batch.ids);
    batch.ids.clear();
    if (!ids.length) {
        return;
    }
    const SANDBOX_ID = window.PERSONALIZATION?.config?.SANDBOX_ID;
    const SANDBOX_NAME = window.PERSONALIZATION?.config?.SANDBOX_NAME;
    const DATASET_ID = window.PERSONALIZATION?.config?.DATASET_ID;
    const identityMap = getIdentityMap();
    try {
        const displayPropositions = [];
        const response = (await batch.alloy('sendEvent', {
            xdm: {
                eventType: 'decisioning.propositionFetch',
                experienceCloud: {
                    platform: {
                        sandboxId: SANDBOX_ID,
                        sandboxName: SANDBOX_NAME
                    }
                },
                ...(Object.keys(identityMap).length > 0 && { identityMap })
            },
            renderDecisions: false,
            personalization: {
                surfaces: ids.map(id => getSurface(pageKey, id)),
                sendDisplayEvent: false
            },
            edgeConfigOverrides: {
                com_adobe_experience_platform: {
                    datasets: {
                        event: {
                            datasetId: DATASET_ID
                        }
                    }
                }
            },
            decisionScopes: ids
        }));
        const propositions = (response.propositions || []);
        const pageCache = pageResultCache.get(pageKey) || new Map();
        pageResultCache.set(pageKey, pageCache);
        await Promise.all(ids.map(async (personalizationId) => {
            const selected = getSelectedProposition(propositions, personalizationId);
            const html = selected
                ? await resolveHtmlFromProposition(selected, displayPropositions)
                : null;
            pageCache.set(personalizationId, html);
            const subscribers = batch.subscribersById.get(personalizationId);
            if (subscribers) {
                subscribers.forEach(notify => {
                    notify(html);
                });
            }
        }));
        if (displayPropositions.length) {
            batch.alloy('sendEvent', {
                xdm: {
                    eventType: 'decisioning.propositionDisplay',
                    _experience: {
                        decisioning: {
                            propositions: displayPropositions,
                            propositionEventType: { display: 1 }
                        }
                    }
                }
            });
        }
    }
    catch {
        ids.forEach(personalizationId => {
            const subscribers = batch.subscribersById.get(personalizationId);
            if (subscribers) {
                subscribers.forEach(notify => {
                    notify(null);
                });
            }
        });
    }
}
function subscribeToBatch(pageKey, personalizationId, alloy, subscriber) {
    let batch = pageBatches.get(pageKey);
    if (!batch) {
        batch = {
            ids: new Set(),
            subscribersById: new Map(),
            alloy,
            flushTimer: null,
            isFlushed: false
        };
        pageBatches.set(pageKey, batch);
        if (typeof window !== 'undefined' && document.readyState !== 'complete') {
            window.addEventListener('load', () => {
                const currentBatch = pageBatches.get(pageKey);
                if (!currentBatch || currentBatch.isFlushed) {
                    return;
                }
                void flushBatch(pageKey);
            }, { once: true });
        }
    }
    batch.alloy = alloy;
    batch.ids.add(personalizationId);
    const subscribers = batch.subscribersById.get(personalizationId) || new Set();
    subscribers.add(subscriber);
    batch.subscribersById.set(personalizationId, subscribers);
    scheduleFlush(pageKey);
    return () => {
        const currentBatch = pageBatches.get(pageKey);
        if (!currentBatch) {
            return;
        }
        const currentSubscribers = currentBatch.subscribersById.get(personalizationId);
        currentSubscribers?.delete(subscriber);
        if (currentSubscribers?.size === 0) {
            currentBatch.subscribersById.delete(personalizationId);
            currentBatch.ids.delete(personalizationId);
        }
        if (!currentBatch.subscribersById.size &&
            !currentBatch.ids.size &&
            !currentBatch.isFlushed) {
            if (currentBatch.flushTimer) {
                clearTimeout(currentBatch.flushTimer);
            }
            pageBatches.delete(pageKey);
        }
    };
}
function useHtmlPersonalization(personalizationId, isEnabled) {
    const alloy = (0, useAlloy_1.useAlloy)();
    const [html, setHtml] = (0, react_1.useState)(null);
    const [isLoading, setIsLoading] = (0, react_1.useState)(true);
    const [isTimedOut, setIsTimedOut] = (0, react_1.useState)(false);
    const waitingForInitialization = !!personalizationId &&
        isEnabled &&
        typeof alloy !== 'function' &&
        !isTimedOut;
    (0, react_1.useEffect)(() => {
        if (!isEnabled || !personalizationId) {
            return;
        }
        setIsTimedOut(false);
        const timer = setTimeout(() => {
            setIsTimedOut(true);
        }, exports.PERSONALIZATION_TIMEOUT_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [personalizationId, isEnabled]);
    (0, react_1.useEffect)(() => {
        // Skip execution if feature flag is disabled or personalizationId is empty
        if (!isEnabled || !personalizationId) {
            setHtml(null);
            setIsLoading(false);
            return;
        }
        if (isTimedOut && typeof alloy !== 'function') {
            setHtml(null);
            setIsLoading(false);
            return;
        }
        // Wait until Alloy is available
        if (typeof alloy !== 'function') {
            setIsLoading(true);
            return;
        }
        // Reset loading state for each new request
        setIsLoading(true);
        const pageKey = getPageKey();
        const cachedHtml = pageResultCache.get(pageKey)?.get(personalizationId);
        if (cachedHtml !== undefined) {
            setHtml(cachedHtml);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        const unsubscribe = subscribeToBatch(pageKey, personalizationId, alloy, resolvedHtml => {
            setHtml(resolvedHtml);
            setIsLoading(false);
        });
        return () => {
            unsubscribe();
        };
    }, [alloy, personalizationId, isEnabled, isTimedOut]);
    return {
        html,
        isLoading: isLoading || (waitingForInitialization && !isTimedOut)
    };
}
exports.useHtmlPersonalization = useHtmlPersonalization;
